/**
 * AI junction guidance - "which way, and which lane", in one sentence.
 *
 * The deterministic rules in nav/lanes.ts are correct but narrow. They can only
 * speak when the map hands them a lane array that survives the safety
 * cross-checks, and measured over six Thai routes that is 63% of forks and
 * ramps but only 6% of ordinary turns. Everywhere else the app is silent, which
 * is safe and unhelpful in equal measure.
 *
 * This layer fills that gap using a cheap model (Haiku, server-side - see
 * api/index.php, route `ai/lanes`) and everything the map DID give us: the
 * banner, the exit number, both road numbers, the signed destinations, the
 * maneuver geometry, and the lane array when there is one. It never invents a
 * junction; the server prompt is explicit about that, and anything the model is
 * unsure of comes back `certain: false` and is dropped here.
 *
 * Two triggers, as agreed:
 *
 *  - PREFETCH, once, when a route is chosen. One call covers every junction on
 *    the route, so the guidance is already in hand at 100 km/h with no latency
 *    and no dependence on signal at the junction itself.
 *  - REFINE, live, for a single junction the rules gave up on or that the
 *    prefetch did not cover (a reroute, a fuel detour, a fork that was added
 *    when the route changed under us).
 *
 * Everything is best-effort. No key, no signal, quota exhausted, a malformed
 * answer: the app falls back to the deterministic guidance it already had. AI
 * guidance is only ever ADDITIVE - it never overrides or suppresses a rule that
 * fired, because the rules are the ones that have been measured.
 */

import type { Lane, RouteStep } from '../core/types';
import type { Lang } from '../core/i18n';
import { stepRefs } from '../nav/roads';

const API = `${import.meta.env.BASE_URL}api`;

export interface LaneGuide {
  /** One line, already in the driver's language. */
  guide: string;
  side: 'left' | 'right' | 'centre' | null;
}

/** What we send per junction. Only facts that came out of the map. */
interface JunctionPayload {
  index: number;
  type: string;
  modifier: string | null;
  /** Road numbers we are leaving and joining. */
  fromRefs: string[];
  toRefs: string[];
  /** Signposted destinations and exit number, when the map has them. */
  destinations: string | null;
  exit: string | null;
  /** Degrees the route swings through the junction; tells a fork from a turn. */
  turnDegrees: number | null;
  isFork: boolean;
  lanes:
    | {
        valid: boolean;
        indications: string[];
      }[]
    | null;
}

/** Lanes at a maneuver's own junction, flattened for the wire. */
function lanePayload(lanes: Lane[] | null): JunctionPayload['lanes'] {
  if (!lanes?.length) return null;
  return lanes.map((l) => ({
    valid: l.valid === true || l.active === true,
    indications: l.indications ?? [],
  }));
}

function turnDegrees(step: RouteStep): number | null {
  const { bearing_before: a, bearing_after: b } = step.maneuver;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  let d = ((b - a + 540) % 360) - 180;
  return Math.round(d);
}

export function junctionPayload(
  index: number,
  prev: RouteStep | null,
  step: RouteStep,
  isFork: boolean,
): JunctionPayload {
  return {
    index,
    type: step.maneuver.type ?? 'turn',
    modifier: step.maneuver.modifier ?? null,
    fromRefs: stepRefs(prev),
    toRefs: stepRefs(step),
    destinations: (step.destinations ?? '').trim() || null,
    exit: (step.exits ?? '').trim() || null,
    turnDegrees: turnDegrees(step),
    isFork,
    lanes: lanePayload(step.intersections?.[0]?.lanes ?? null),
  };
}

interface GuideResponse {
  guides?: { index: number; guide?: string; side?: string | null; certain?: boolean }[];
}

/**
 * Junctions worth asking about.
 *
 * Not every step: `depart`, `arrive` and the long "continue" runs need no
 * guidance, and each one in the batch costs tokens. A junction qualifies when
 * it is a branch (fork or ramp - the ones with real lane data), or when it
 * carries lanes, an exit number or signed destinations to reconcile.
 */
export function isGuidableJunction(step: RouteStep): boolean {
  const type = (step.maneuver.type ?? '').toLowerCase();
  if (type === 'depart' || type === 'arrive') return false;
  const mod = (step.maneuver.modifier ?? '').toLowerCase();
  const branch =
    type === 'fork' ||
    type === 'off ramp' ||
    type === 'on ramp' ||
    mod === 'slight left' ||
    mod === 'slight right';
  if (branch) return true;
  if (step.intersections?.[0]?.lanes?.length) return true;
  return !!(step.exits ?? '').trim() || !!(step.destinations ?? '').trim();
}

async function ask(
  junctions: JunctionPayload[],
  lang: Lang,
  drivingSide: 'left' | 'right',
  signal?: AbortSignal,
): Promise<Map<number, LaneGuide>> {
  const out = new Map<number, LaneGuide>();
  if (!junctions.length) return out;

  const res = await fetch(`${API}/ai/lanes`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lang, drivingSide, junctions }),
    signal: signal ?? AbortSignal.timeout(30_000),
  });
  // 503 no key, 429 over the daily cap, 401 signed out. All non-events: the
  // deterministic guidance is still there and the driver loses nothing.
  if (!res.ok) return out;

  const body = (await res.json()) as GuideResponse;
  for (const g of body.guides ?? []) {
    // An uncertain answer is discarded here rather than shown hedged. Half a
    // junction guide at 100 km/h is worse than none.
    if (!g.certain || typeof g.guide !== 'string') continue;
    const guide = g.guide.trim();
    if (!guide) continue;
    const side =
      g.side === 'left' || g.side === 'right' || g.side === 'centre' ? g.side : null;
    out.set(g.index, { guide, side });
  }
  return out;
}

/**
 * Guidance for a whole route, cached by step index.
 *
 * Built once per route. `replaceRoute` on a reroute makes a new one rather than
 * patching this one - the indices belong to a specific step list, and reusing
 * them across routes is how guidance ends up attached to the wrong junction.
 */
export class LaneGuideCache {
  private guides = new Map<number, LaneGuide>();
  private pending = new Set<number>();
  private prefetched = false;

  /** Guidance for step `index`, or null. Never throws, never blocks. */
  get(index: number): LaneGuide | null {
    return this.guides.get(index) ?? null;
  }

  /** One call covering every guidable junction on the route. */
  async prefetch(
    steps: RouteStep[],
    lang: Lang,
    drivingSide: 'left' | 'right',
    signal?: AbortSignal,
  ): Promise<void> {
    if (this.prefetched) return;
    this.prefetched = true;

    const payload: JunctionPayload[] = [];
    for (let i = 1; i < steps.length; i++) {
      if (!isGuidableJunction(steps[i])) continue;
      payload.push(junctionPayload(i, steps[i - 1] ?? null, steps[i], true));
    }
    if (!payload.length) return;

    try {
      const got = await ask(payload.slice(0, 40), lang, drivingSide, signal);
      for (const [i, g] of got) this.guides.set(i, g);
    } catch {
      /* best-effort by design */
    }
  }

  /**
   * Live refinement for one junction the prefetch missed or the rules rejected.
   * Deduped by index so an approach that spans many GPS fixes asks once.
   */
  async refine(
    index: number,
    prev: RouteStep | null,
    step: RouteStep,
    lang: Lang,
    drivingSide: 'left' | 'right',
  ): Promise<void> {
    if (this.guides.has(index) || this.pending.has(index)) return;
    this.pending.add(index);
    try {
      const got = await ask(
        [junctionPayload(index, prev, step, true)],
        lang,
        drivingSide,
      );
      for (const [i, g] of got) this.guides.set(i, g);
    } catch {
      /* best-effort by design */
    } finally {
      this.pending.delete(index);
    }
  }
}
